import * as React from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button as ShadcnButton } from "@/components/ui/button";
import { fluentIconComponent } from "@/components/fluent-icon-map";
import {
  ContextualMenu,
  type IContextualMenuItem,
} from "@/components/fluent-contextualmenu-compat";

// Icon resolution goes through the shared `fluentIconComponent` so the
// missing-icon console warning fires consistently for all shims.
const renderIcon = (iconName?: string): React.ReactNode => {
  if (!iconName) return null;
  const Icon = fluentIconComponent(iconName);
  return Icon ? <Icon size={16} /> : null;
};

// Spinning loader that replaces the leading icon when `loading={true}`.
const LoaderIcon: React.FC = () => (
  <Loader2 size={16} className="animate-spin" aria-hidden />
);

interface FluentBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  // Fluent's IIconProps carries iconName plus arbitrary style/className/etc.
  // We only read iconName; everything else is silently ignored.
  iconProps?: { iconName?: string; [key: string]: any };
  ariaLabel?: string;
  styles?: any; // Fluent CSS-in-JS — intentionally dropped.
  // Fluent hangs a context menu off the button; rendered via the
  // ContextualMenu shim (see `useButtonMenu`).
  menuProps?: { items?: IContextualMenuItem[]; [key: string]: any };
  componentRef?: any;
  primary?: boolean;
  split?: boolean;
  splitButtonAriaLabel?: string;
  iconType?: any;
  href?: string;
  target?: string;
  rel?: string;
  variant?: any;
  // Allow Fluent-style render overrides without TS complaints.
  onRenderText?: any;
  onRenderIcon?: any;
  onRenderMenuIcon?: any;
  toggle?: boolean;
  checked?: boolean;
  allowDisabledFocus?: boolean;
  // Fluent's `elementRef` is callers' way to grab the underlying DOM node
  // (e.g. as a Callout / TooltipHost target). We forward it to the rendered
  // button so popover anchoring keeps working.
  elementRef?: (ref: HTMLButtonElement | null) => void;
  /**
   * shadcn-style async-action flavor: while `true`, the leading icon is
   * replaced by a spinning `Loader2` and the button is forced into the
   * `disabled` state. Useful for login/submit buttons that wait on a network
   * round-trip — prevents double-submits and signals progress.
   */
  loading?: boolean;
}

// Strip the Fluent-only props so shadcn Button only sees standard HTML attrs.
function pickHtmlProps(p: FluentBaseProps): React.ButtonHTMLAttributes<HTMLButtonElement> & {
  "aria-label"?: string;
} {
  const {
    text,
    iconProps,
    ariaLabel,
    styles,
    menuProps,
    componentRef,
    primary,
    split,
    splitButtonAriaLabel,
    iconType,
    onRenderText,
    onRenderIcon,
    onRenderMenuIcon,
    toggle,
    checked,
    allowDisabledFocus,
    variant,
    children,
    elementRef,
    loading,
    ...rest
  } = p;
  const out: any = { ...rest };
  if (ariaLabel !== undefined) out["aria-label"] = ariaLabel;
  // When loading, force the button into a disabled state so the click can't
  // re-fire. Preserve an explicit caller-supplied `disabled` if it's truthy.
  if (loading) out.disabled = true;
  return out;
}

const renderBody = (p: FluentBaseProps): React.ReactNode => {
  // When `loading`, swap the leading icon for a spinner. Label stays so the
  // button keeps its width and the user sees what action is pending.
  const icon = p.loading ? <LoaderIcon /> : renderIcon(p.iconProps?.iconName);
  const label = p.text ?? p.children;
  if (!icon) return label;
  if (label === undefined || label === null || label === "") return icon;
  return (
    <>
      {icon}
      {label}
    </>
  );
};

// Combine a forwardRef's ref with Fluent's `elementRef` callback so callers
// using either mechanism receive the underlying DOM node. `local` is the
// shim's own ref (used to anchor the context menu).
function combineRefs(
  forwarded: React.ForwardedRef<HTMLButtonElement>,
  fluentRef?: (ref: HTMLButtonElement | null) => void,
  local?: React.MutableRefObject<HTMLButtonElement | null>,
) {
  return (node: HTMLButtonElement | null) => {
    if (typeof forwarded === "function") forwarded(node);
    else if (forwarded && "current" in forwarded) {
      (forwarded as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    }
    if (local) local.current = node;
    fluentRef?.(node);
  };
}

// Fluent buttons open `menuProps` as a context menu on click; the shim wires
// that to the ContextualMenu shim. The button keeps its own `onClick` (Fluent
// fires both), toggles the menu open, and the menu dismisses itself when an
// item is picked or the user clicks away.
function useButtonMenu(props: FluentBaseProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const items = (props.menuProps?.items ?? []) as IContextualMenuItem[];
  const hasMenu = items.length > 0;

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    props.onClick?.(event);
    if (hasMenu) setOpen((value) => !value);
  };

  const menu =
    hasMenu && open ? (
      <ContextualMenu
        {...props.menuProps}
        items={items}
        target={triggerRef.current}
        onDismiss={(event?: any) => {
          setOpen(false);
          props.menuProps?.onDismiss?.(event);
        }}
      />
    ) : null;

  // Props the button itself needs: menus are toggled, not navigated to, so the
  // trigger advertises itself as a popup opener for assistive technology.
  const triggerProps = hasMenu
    ? { onClick, "aria-haspopup": true as const, "aria-expanded": open }
    : { onClick };

  return { triggerRef, hasMenu, triggerProps, menu };
}

// Chevron appended to a button that owns a menu, matching Fluent's menu icon.
const MenuChevron: React.FC = () => (
  <ChevronDown size={14} className="opacity-60" aria-hidden />
);

// All Fluent buttons used to render around 32px tall — close to shadcn's `sm`
// (h-9). Anchoring all four variants there keeps the visual rhythm consistent
// with the rest of the app, which still uses Fluent inputs / dropdowns sized
// at the same scale. IconButton drops further to h-8 w-8 to match Fluent's
// compact icon-button proportions.

// Shared body for the text-label variants — they differ only in the shadcn
// variant they map to.
const TextButton = React.forwardRef<
  HTMLButtonElement,
  FluentBaseProps & { shadcnVariant: "default" | "outline" | "ghost" }
>(({ shadcnVariant, ...props }, ref) => {
  const { triggerRef, hasMenu, triggerProps, menu } = useButtonMenu(props);

  return (
    <>
      <ShadcnButton
        ref={combineRefs(ref, props.elementRef, triggerRef)}
        {...pickHtmlProps(props)}
        {...triggerProps}
        variant={shadcnVariant}
        size="sm"
        className={cn("gap-2", props.className)}
        type={props.type ?? "button"}
      >
        {renderBody(props)}
        {hasMenu && <MenuChevron />}
      </ShadcnButton>
      {menu}
    </>
  );
});
TextButton.displayName = "TextButton";

export const PrimaryButton = React.forwardRef<HTMLButtonElement, FluentBaseProps>(
  (props, ref) => <TextButton ref={ref} {...props} shadcnVariant="default" />,
);
PrimaryButton.displayName = "PrimaryButton";

export const DefaultButton = React.forwardRef<HTMLButtonElement, FluentBaseProps>(
  (props, ref) => <TextButton ref={ref} {...props} shadcnVariant="outline" />,
);
DefaultButton.displayName = "DefaultButton";

export const ActionButton = React.forwardRef<HTMLButtonElement, FluentBaseProps>(
  (props, ref) => <TextButton ref={ref} {...props} shadcnVariant="ghost" />,
);
ActionButton.displayName = "ActionButton";

export const IconButton = React.forwardRef<HTMLButtonElement, FluentBaseProps>(
  (props, ref) => {
    // No chevron here — Fluent's icon buttons keep their single glyph.
    const { triggerRef, triggerProps, menu } = useButtonMenu(props);

    return (
      <>
        <ShadcnButton
          ref={combineRefs(ref, props.elementRef, triggerRef)}
          {...pickHtmlProps(props)}
          {...triggerProps}
          variant="ghost"
          size="icon"
          title={props.title}
          className={cn("h-8 w-8", props.className)}
          type={props.type ?? "button"}
        >
          {props.loading
            ? <LoaderIcon />
            : (renderIcon(props.iconProps?.iconName) ?? props.children)}
        </ShadcnButton>
        {menu}
      </>
    );
  },
);
IconButton.displayName = "IconButton";
